import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import ShubhHeader from './ShubhHeader';
import ShubhBottomNav from './ShubhBottomNav';
import { 
  User, Plus, Edit2, Trash2, Calendar, Clock, MapPin, 
  Star, ChevronRight, X, Check, AlertCircle
} from 'lucide-react';
import '../../styles/shubh-design-system.css';

import { BACKEND_URL } from '../../lib/backend';

const API_URL = BACKEND_URL;

// Zodiac signs
const ZODIAC_SIGNS = [
  { name: 'Aries', hindi: 'मेष', symbol: '♈\uFE0E', color: '#FFE4E1' },
  { name: 'Taurus', hindi: 'वृषभ', symbol: '♉\uFE0E', color: '#E8F5E9' },
  { name: 'Gemini', hindi: 'मिथुन', symbol: '♊\uFE0E', color: '#FFF8E1' },
  { name: 'Cancer', hindi: 'कर्क', symbol: '♋\uFE0E', color: '#E3F2FD' },
  { name: 'Leo', hindi: 'सिंह', symbol: '♌\uFE0E', color: '#FFF3E0' },
  { name: 'Virgo', hindi: 'कन्या', symbol: '♍\uFE0E', color: '#F3E5F5' },
  { name: 'Libra', hindi: 'तुला', symbol: '♎\uFE0E', color: '#FCE4EC' },
  { name: 'Scorpio', hindi: 'वृश्चिक', symbol: '♏\uFE0E', color: '#FFEBEE' },
  { name: 'Sagittarius', hindi: 'धनु', symbol: '♐\uFE0E', color: '#E8EAF6' },
  { name: 'Capricorn', hindi: 'मकर', symbol: '♑\uFE0E', color: '#EFEBE9' },
  { name: 'Aquarius', hindi: 'कुंभ', symbol: '♒\uFE0E', color: '#E0F7FA' },
  { name: 'Pisces', hindi: 'मीन', symbol: '♓\uFE0E', color: '#EDE7F6' },
];

const getZodiacInfo = (signName) => {
  const sign = ZODIAC_SIGNS.find(z => 
    z.name.toLowerCase() === signName?.toLowerCase()
  );
  return sign || { name: signName, symbol: '★', color: '#FFF8E1', hindi: '' };
};

const ShubhProfilesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isHindi = language === 'hi';
  
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    time_of_birth: '',
    place_of_birth: ''
  });

  // Fetch profiles
  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/profiles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfiles(Array.isArray(data) ? data : (data.profiles || []));
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/profiles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        await fetchProfiles();
        setShowCreateModal(false);
        setFormData({ name: '', date_of_birth: '', time_of_birth: '', place_of_birth: '' });
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to create profile');
      }
    } catch (err) {
      setError('Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/profiles/${profileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchProfiles();
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Error deleting profile:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(isHindi ? 'hi-IN' : 'en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="shubh-page" style={{ background: 'var(--shubh-ivory)' }}>
      <ShubhHeader title={isHindi ? 'प्रोफ़ाइल' : 'Profiles'} />

      <div className="shubh-page-content" style={{ paddingTop: 'var(--space-4)' }}>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h2 
            className="text-xl font-medium mb-1"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--ink-dark)' }}
          >
            {isHindi ? 'आपके परिवार' : 'Your Family'}
          </h2>
          <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
            {isHindi 
              ? `${profiles.length}/5 प्रोफ़ाइल बनाई गई`
              : `${profiles.length}/5 profiles created`
            }
          </p>
        </motion.div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="shubh-skeleton h-24 rounded-xl" />
            ))}
          </div>
        ) : (
          <>
            {/* Add New Profile Card */}
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setShowCreateModal(true)}
              className="w-full p-4 rounded-2xl border-2 border-dashed mb-4 flex items-center justify-center gap-3 transition-all hover:border-solid"
              style={{ 
                borderColor: 'var(--saffron)',
                background: 'rgba(245, 185, 66, 0.05)'
              }}
              disabled={profiles.length >= 5}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(245, 185, 66, 0.2)' }}
              >
                <Plus className="w-6 h-6" style={{ color: 'var(--saffron-dark)' }} />
              </div>
              <div className="text-left">
                <p className="font-medium" style={{ color: 'var(--ink-dark)' }}>
                  {isHindi ? 'नई प्रोफ़ाइल जोड़ें' : 'Add New Profile'}
                </p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                  {profiles.length >= 5 
                    ? (isHindi ? 'अधिकतम सीमा पूरी' : 'Maximum limit reached')
                    : (isHindi ? 'परिवार का सदस्य जोड़ें' : 'Add a family member')
                  }
                </p>
              </div>
            </motion.button>

            {/* Profile Cards */}
            <div className="space-y-3">
              {profiles.map((profile, index) => {
                const zodiac = getZodiacInfo(profile.sun_sign);
                
                return (
                  <motion.div
                    key={profile.profile_id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="shubh-card p-4"
                  >
                    <div className="flex items-start gap-4">
                      {/* Avatar with Zodiac */}
                      <div 
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
                        style={{ background: zodiac.color }}
                      >
                        {zodiac.symbol}
                      </div>

                      {/* Profile Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold truncate" style={{ color: 'var(--ink-dark)' }}>
                            {profile.name}
                          </h3>
                          {profile.sun_sign && (
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ 
                                background: 'rgba(245, 185, 66, 0.15)',
                                color: 'var(--saffron-dark)'
                              }}
                            >
                              {isHindi ? zodiac.hindi : zodiac.name}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                          {profile.date_of_birth && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(profile.date_of_birth)}
                            </span>
                          )}
                          {profile.time_of_birth && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {profile.time_of_birth}
                            </span>
                          )}
                        </div>

                        {profile.place_of_birth && (
                          <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'var(--ink-light)' }}>
                            <MapPin className="w-3 h-3" />
                            {profile.place_of_birth}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/janam-kundli?profile=${profile.profile_id}`)}
                          className="p-2 rounded-xl"
                          style={{ background: 'var(--warm-beige)' }}
                        >
                          <Star className="w-4 h-4" style={{ color: 'var(--saffron-dark)' }} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(profile.profile_id)}
                          className="p-2 rounded-xl"
                          style={{ background: 'rgba(239, 68, 68, 0.1)' }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Empty State */}
            {profiles.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div 
                  className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{ background: 'var(--warm-beige)' }}
                >
                  <User className="w-10 h-10" style={{ color: 'var(--ink-muted)' }} />
                </div>
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--ink-dark)' }}>
                  {isHindi ? 'कोई प्रोफ़ाइल नहीं' : 'No Profiles Found'}
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--ink-light)' }}>
                  {isHindi 
                    ? 'अपनी जन्म कुंडली बनाने के लिए प्रोफ़ाइल बनाएं'
                    : 'Create a profile to generate your Janam Kundli'
                  }
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="shubh-btn shubh-btn-primary"
                >
                  <Plus className="w-5 h-5" />
                  {isHindi ? 'प्रोफ़ाइल बनाएं' : 'Create Profile'}
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Create Profile Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-lg rounded-t-3xl p-6"
              style={{ 
                background: 'var(--shubh-ivory)',
                paddingBottom: 'calc(24px + env(safe-area-inset-bottom))'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 
                  className="text-xl font-medium"
                  style={{ fontFamily: 'var(--font-display)', color: 'var(--ink-dark)' }}
                >
                  {isHindi ? 'नई प्रोफ़ाइल बनाएं' : 'Create New Profile'}
                </h2>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 rounded-full"
                  style={{ background: 'var(--warm-beige)' }}
                >
                  <X className="w-5 h-5" style={{ color: 'var(--ink-dark)' }} />
                </button>
              </div>

              <form onSubmit={handleCreateProfile} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--ink-dark)' }}>
                    {isHindi ? 'पूरा नाम' : 'Full Name'} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={isHindi ? 'नाम दर्ज करें' : 'Enter name'}
                    className="shubh-input"
                    required
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--ink-dark)' }}>
                      {isHindi ? 'जन्म तिथि' : 'Date of Birth'} *
                    </label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      className="shubh-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--ink-dark)' }}>
                      {isHindi ? 'जन्म समय' : 'Time of Birth'}
                    </label>
                    <input
                      type="time"
                      value={formData.time_of_birth}
                      onChange={(e) => setFormData(prev => ({ ...prev, time_of_birth: e.target.value }))}
                      className="shubh-input"
                    />
                  </div>
                </div>

                {/* Place */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--ink-dark)' }}>
                    {isHindi ? 'जन्म स्थान' : 'Birth Place'} *
                  </label>
                  <input
                    type="text"
                    value={formData.place_of_birth}
                    onChange={(e) => setFormData(prev => ({ ...prev, place_of_birth: e.target.value }))}
                    placeholder={isHindi ? 'उदा: मुंबई, भारत' : 'e.g. Mumbai, India'}
                    className="shubh-input"
                    required
                  />
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <p className="text-sm text-red-500">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={saving}
                  className="shubh-btn shubh-btn-primary w-full mt-4"
                >
                  {saving ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {isHindi ? 'प्रोफ़ाइल बनाएं' : 'Create Profile'}
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm rounded-2xl p-6"
              style={{ background: 'var(--pure-white)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div 
                  className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{ background: 'rgba(239, 68, 68, 0.1)' }}
                >
                  <Trash2 className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--ink-dark)' }}>
                  {isHindi ? 'प्रोफ़ाइल हटाएं?' : 'Delete Profile?'}
                </h3>
                <p className="text-sm mb-6" style={{ color: 'var(--ink-light)' }}>
                  {isHindi 
                    ? 'यह क्रिया पूर्ववत नहीं की जा सकती।'
                    : 'This action cannot be undone.'
                  }
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 shubh-btn shubh-btn-secondary"
                  >
                    {isHindi ? 'रद्द करें' : 'Cancel'}
                  </button>
                  <button
                    onClick={() => handleDeleteProfile(deleteConfirm)}
                    className="flex-1 shubh-btn"
                    style={{ background: '#EF4444', color: 'white' }}
                  >
                    {isHindi ? 'हटाएं' : 'Delete'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ShubhBottomNav />
    </div>
  );
};

export default ShubhProfilesPage;
